import { createContext, useCallback, useContext, useRef, useState } from 'react';
import CatalogModal from '../components/SeedInventory/CatalogModal';
import EditSeedModal from '../components/SeedInventory/EditSeedModal';
import { Toast } from '../components/ui';
import { supabase } from '../supabaseClient';
import { normalizeHarvestSeason } from '../utils/plantingScheduleUtils';
import { buildSeedUpdateFromFormData } from '../utils';

const CatalogModalContext = createContext(null);

/**
 * Provides a globally-accessible "open catalog modal" function.
 *
 * Usage:
 *   const { openCatalogModal } = useCatalogModal();
 *   openCatalogModal((seed) => console.log('selected', seed));
 *
 * The optional onSelect callback receives the inserted seed row after the user
 * picks a plant. Pass null/undefined when you just want to browse.
 */
export const CatalogModalProvider = ({ children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [toast, setToast] = useState(null); // { message, seed } — lives here so it outlives modal close
    const [editSeed, setEditSeed] = useState(null);       // seed record being edited
    const [editFormData, setEditFormData] = useState(null); // form state for EditSeedModal
    // useRef avoids stale-closure issues — always holds the latest callback
    const onSelectRef = useRef(null);
    // When true, the modal stays open after each selection (for multi-add flows)
    const stayOpenRef = useRef(false);

    const openCatalogModal = useCallback((onSelect, options = {}) => {
        onSelectRef.current = onSelect ?? null;
        stayOpenRef.current = options.stayOpen ?? false;
        setIsOpen(true);
    }, []);

    const closeCatalogModal = useCallback(() => {
        setIsOpen(false);
        onSelectRef.current = null;
        stayOpenRef.current = false;
    }, []);

    const handleSelect = useCallback((seedRecord) => {
        onSelectRef.current?.(seedRecord);
        if (!stayOpenRef.current) {
            closeCatalogModal();
        }
    }, [closeCatalogModal]);

    const openEditModal = useCallback((seed) => {
        setEditFormData({
            plant: seed.plant,
            variety: seed.variety || '',
            quantity: seed.quantity || '',
            season: seed.season || '',
            category: seed.category || '',
            need_to_buy: seed.need_to_buy || false,
            notes: seed.notes || '',
            type: seed.type || 'seed',
            days_to_production: seed.days_to_production || '',
            weeks_indoors_before_transplant: seed.weeks_indoors_before_transplant || '',
            preferred_start_method: seed.preferred_start_method || '',
            harvest_season: normalizeHarvestSeason(seed.harvest_season),
            years_to_production: seed.years_to_production || '',
            planned_start_date: seed.planned_start_date || '',
            planned_transplant_date: seed.planned_transplant_date || '',
            frost_tolerant: seed.frost_tolerant || false,
            spacing: seed.spacing || '',
            icon: seed.icon || '',
            sun_requirement: seed.sun_requirement || '',
            water_needs: seed.water_needs || '',
            direct_sow_weeks_before_last_frost: seed.direct_sow_weeks_before_last_frost != null ? String(seed.direct_sow_weeks_before_last_frost) : '',
        });
        setEditSeed(seed);
        setToast(null);
    }, []);

    const handleEditSave = useCallback(async () => {
        if (!editSeed || !editFormData) return;
        const fd = editFormData;
        const seedData = buildSeedUpdateFromFormData(fd, editSeed.icon);

        const { data: updatedSeed, error } = await supabase
            .from('seeds')
            .update(seedData)
            .eq('id', editSeed.id)
            .select()
            .single();

        if (error) {
            alert('Error updating plant details: ' + error.message);
            return;
        }

        // Broadcast so any visible page (Seeds, Layout, etc.) picks up the update.
        // seed-added-externally causes SeedInventory to reload and re-broadcast
        // seeds-updated with the full array, which all other consumers listen for.
        window.dispatchEvent(new CustomEvent('seed-added-externally', { detail: { seed: updatedSeed } }));

        setEditSeed(null);
        setEditFormData(null);
    }, [editSeed, editFormData]);

    return (
        <CatalogModalContext.Provider value={{ openCatalogModal, closeCatalogModal }}>
            {children}
            <CatalogModal isOpen={isOpen} onSelect={handleSelect} onClose={closeCatalogModal} onToast={setToast} />
            {toast && (
                <Toast
                    message={toast.message}
                    onEditDetails={() => openEditModal(toast.seed)}
                    onDismiss={() => setToast(null)}
                />
            )}
            <EditSeedModal
                isOpen={!!editSeed}
                formData={editFormData || {}}
                onFormChange={setEditFormData}
                onSave={handleEditSave}
                onClose={() => { setEditSeed(null); setEditFormData(null); }}
                zIndex="z-[80]"
            />
        </CatalogModalContext.Provider>
    );
};

export const useCatalogModal = () => {
    const ctx = useContext(CatalogModalContext);
    if (!ctx) throw new Error('useCatalogModal must be used within CatalogModalProvider');
    return ctx;
};
